import { ref, readonly, type Ref } from 'vue';
import type {
  ContactUsSectionModel,
  ContactUsSectionsResponse,
} from '../../../api/interfaces/contactUsSectionModel';
import { apiCall } from '../../../api/services/apiCalls';
import { fallbackContactUsSections } from '../../../common/shared';

export function useFetchSections() {
  const contactUsSections: Ref<ContactUsSectionModel[]> = ref([]);
  const isLoading: Ref<boolean> = ref(false);
  const error: Ref<string | null> = ref(null);

  // Hardcoded section names to exclude from dynamic sections
  const excludedSectionNames = [];

  // Filter out sections that match hardcoded section names (case-insensitive)
  const filterExcludedSections = (sections: ContactUsSectionModel[]): ContactUsSectionModel[] => sections.filter((section) => {
    const sectionNameLower = section.section.toLowerCase();
    return !excludedSectionNames.some((excluded) => excluded.toLowerCase() === sectionNameLower);
  });

  // Fetch contact us sections from the api
  const fetchContactUsSections = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await apiCall('default', 'contact-us-section');

      if (response && Array.isArray(response)) {
        const allSections = response as ContactUsSectionsResponse;
        // Filter out hardcoded sections to prevent duplicates
        contactUsSections.value = filterExcludedSections(allSections);
      } else {
        throw new Error('Invalid response format from contact-us-section API');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch contact us sections';
      error.value = errorMessage;
      console.error('Error fetching contact us sections:', err);

      // use fallback data when API fails (also filter fallback data)
      contactUsSections.value = filterExcludedSections(fallbackContactUsSections);
    } finally {
      isLoading.value = false;
    }
  };

  // clearing out the state
  const reset = (): void => {
    contactUsSections.value = [];
    isLoading.value = false;
    error.value = null;
  };

  return {
    contactUsSections: readonly(contactUsSections),
    isLoading: readonly(isLoading),
    error: readonly(error),
    fetchContactUsSections,
    reset,
    excludedSectionNames: readonly(ref(excludedSectionNames)),
  };
}
